import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Tag, 
  Button, 
  Spin, 
  Empty,
  message,
  Tooltip
} from 'antd';
import { 
  ArrowLeftOutlined, 
  LockOutlined, 
  UnlockOutlined,
  CalendarOutlined,
  TagOutlined,
  EditOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { blogApi } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import './BlogDetail.css';

const BlogDetail = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { authenticated } = useAuth();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(false);

  // 获取博客详情
  const fetchBlogDetail = async () => {
    setLoading(true);
    try {
      const response = await blogApi.getDetail(uuid);
      if (response.code === 200) {
        setBlog(response.data);
      }
    } catch (error) {
      message.error('获取博客详情失败：' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogDetail();
  }, [uuid]);

  // 返回博客列表
  const handleBack = () => {
    navigate('/blogs');
  };

  // 编辑博客
  const handleEdit = () => {
    navigate(`/blog/edit/${uuid}`);
  };

  // 删除博客
  const handleDelete = async () => {
    // 这里应该添加确认对话框
    try {
      await blogApi.delete(uuid);
      message.success('删除成功');
      navigate('/blogs');
    } catch (error) {
      message.error('删除失败：' + error.message);
    }
  };

  // 渲染 Markdown 内容（简单实现）
  const renderContent = (content) => {
    if (!content) return null;
    // 这里可以集成 react-markdown 等库来渲染 Markdown
    return (
      <div className="blog-content-text">
        {content.split('\n').map((paragraph, index) => (
          <p key={index}>{paragraph}</p>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="blog-detail-container">
        <div className="blog-detail-loading">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="blog-detail-container">
        <div className="blog-detail-header">
          <Tooltip title="返回列表">
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={handleBack}
              className="back-btn"
            >
              返回
            </Button>
          </Tooltip>
        </div>
        <Empty description="博客不存在或已被删除" />
      </div>
    );
  }

  return (
    <div className="blog-detail-container">
      {/* 顶部导航 */}
      <div className="blog-detail-header">
        <Tooltip title="返回列表">
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleBack}
            className="back-btn"
          >
            返回
          </Button>
        </Tooltip>
        
        {authenticated && (
          <div className="header-actions">
            <Tooltip title="编辑">
              <Button 
                icon={<EditOutlined />} 
                onClick={handleEdit}
                className="action-btn"
              >
                编辑
              </Button>
            </Tooltip>
            <Tooltip title="删除">
              <Button 
                danger
                icon={<DeleteOutlined />} 
                onClick={handleDelete}
                className="action-btn"
              >
                删除
              </Button>
            </Tooltip>
          </div>
        )}
      </div>

      {/* 博客内容 */}
      <Card className="blog-detail-card">
        {/* 标题区域 */}
        <div className="blog-detail-title-section">
          <h1 className="blog-detail-title">
            {blog.title}
            {blog.is_private ? (
              <LockOutlined className="privacy-icon private" />
            ) : (
              <UnlockOutlined className="privacy-icon public" />
            )}
          </h1>
        </div>

        {/* 元信息 */}
        <div className="blog-detail-meta">
          <span className="meta-item">
            <CalendarOutlined />
            创建于：{new Date(blog.created_at).toLocaleString('zh-CN')}
          </span>
          <span className="meta-item">
            <CalendarOutlined />
            更新于：{new Date(blog.updated_at).toLocaleString('zh-CN')}
          </span>
        </div>

        {/* 标签 */}
        {blog.tags && blog.tags.length > 0 && (
          <div className="blog-detail-tags">
            <TagOutlined className="tags-icon" />
            {blog.tags.map((tag, index) => (
              <Tag key={index} className="blog-tag">{tag}</Tag>
            ))}
          </div>
        )}

        {/* 内容 */}
        <div className="blog-detail-content">
          {renderContent(blog.content)}
        </div>
      </Card>
    </div>
  );
};

export default BlogDetail;
